import { Button, useDropdownDirection } from '@consigli/facade';
import { type ProjectParticipant, type SpeakerAssignScope } from '@consigli/types';
import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';

import { isGenericSpeakerName } from './action-items-table-utils';
import { ChangeSpeakerPopover } from './change-speaker-popover';

import type { ParsedTranscriptEntry } from '../utils/export-types';

interface TranscriptEntryProps {
  entry: ParsedTranscriptEntry;
  participants: ProjectParticipant[];
  onAssignSpeaker: (
    entry: ParsedTranscriptEntry,
    meetingUserId: string,
    scope: SpeakerAssignScope,
  ) => Promise<void>;
}

// Memoized: a transcript can hold hundreds/thousands of segments, and one
// optimistic speaker edit replaces the whole parsed array — without memo every
// row (each now carrying its own hook stack) would re-render on any such change.
export const TranscriptEntry = memo(function TranscriptEntry({
  entry,
  participants,
  onAssignSpeaker,
}: TranscriptEntryProps) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const openDirection = useDropdownDirection({
    containerRef,
    isOpen,
    dropdownHeight: 440,
  });

  const closePopover = useCallback(() => {
    if (isSaving) return;
    setIsOpen(false);
  }, [isSaving]);

  const speakerDisplay = isGenericSpeakerName(entry.speaker)
    ? (() => {
        const num = entry.speaker.match(/\d+/)?.[0];
        const prefix = t('meeting-secretary.action-items.speaker-prefix');
        return num ? `${prefix} ${num}` : entry.speaker;
      })()
    : entry.speaker;

  const canEdit = Boolean(entry.segmentId);

  // An unassigned segment batches by its pipeline label (all_same_name); an
  // already-assigned one batches by the user it is attributed to (all_same_user).
  const isAssigned = entry.assignedMeetingUserId != null;
  const batchScope: 'all_same_name' | 'all_same_user' = isAssigned
    ? 'all_same_user'
    : 'all_same_name';
  const canApplyToAll = isAssigned || entry.speakerName != null;

  const handleSave = useCallback(
    async (meetingUserId: string, scope: SpeakerAssignScope) => {
      setIsSaving(true);
      try {
        await onAssignSpeaker(entry, meetingUserId, scope);
        toast.success(t('meeting-secretary.meetings.speaker-updated'));
        setIsOpen(false);
      } catch {
        toast.error(t('meeting-secretary.meetings.error-updating-speaker'));
      } finally {
        setIsSaving(false);
      }
    },
    [entry, onAssignSpeaker, t],
  );

  return (
    <div className="group border-l-4 border-slate-300 pl-4 py-2">
      <div className="flex items-center space-x-3 mb-2">
        <div className="relative flex items-center gap-1" ref={containerRef}>
          <span className="font-medium text-gray-900">{speakerDisplay}</span>
          {canEdit && (
            <Button
              tertiary
              iconOnly
              icon={FiEdit2}
              iconSize={13}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen((prev) => !prev);
              }}
              className={`!p-1 text-gray-400 hover:text-brand transition-all focus:opacity-100 group-hover:opacity-100 ${
                isOpen ? 'opacity-100' : 'opacity-0'
              }`}
              title={t('meeting-secretary.meetings.change-speaker')}
              aria-label={t('meeting-secretary.meetings.change-speaker')}
            />
          )}
          {isOpen && (
            <ChangeSpeakerPopover
              currentSpeakerLabel={speakerDisplay}
              currentAssignedUserId={entry.assignedMeetingUserId}
              canApplyToAll={canApplyToAll}
              batchScope={batchScope}
              participants={participants}
              isSaving={isSaving}
              direction={openDirection}
              anchorRef={containerRef}
              onSave={handleSave}
              onClose={closePopover}
            />
          )}
        </div>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {entry.startTime}
        </span>
      </div>
      <p className="text-gray-700 leading-relaxed">{entry.text}</p>
    </div>
  );
});
